import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Settings,
  Users,
  UserCog,
  CalendarRange,
  ScanLine,
  ShieldAlert,
  LogOut,
  Megaphone,
  MessageSquareHeart,
  Ticket,
  Crown,
  Mic,
  Trophy,
  Menu,
  X,
  ParkingCircle,
  HelpCircle,
  Sparkles,
} from "lucide-react";
import { useState } from "react";
import { Logo } from "../common/Logo";
import { useAuth } from "../../contexts/AuthContext";

const items = [
  { to: "/admin", icon: LayoutDashboard, label: "Operations", testid: "admin-nav-ops", end: true },
  { to: "/admin/bookings", icon: Ticket, label: "Bookings", testid: "admin-nav-bookings" },
  { to: "/admin/users", icon: UserCog, label: "Users", testid: "admin-nav-users" },
  { to: "/admin/registry", icon: Users, label: "Registry", testid: "admin-nav-registry" },
  { to: "/admin/vvip-tracker", icon: Crown, label: "VVIP Tracker", testid: "admin-nav-vvip" },
  { to: "/admin/scanner", icon: ScanLine, label: "QR Scanner", testid: "admin-nav-scanner" },
  { to: "/admin/artists", icon: Mic, label: "Artists", testid: "admin-nav-artists" },
  { to: "/admin/competitions", icon: Trophy, label: "Competitions", testid: "admin-nav-competitions" },
  { to: "/admin/activities", icon: CalendarRange, label: "Activities", testid: "admin-nav-activities" },
  { to: "/admin/announcements", icon: Megaphone, label: "Announcements", testid: "admin-nav-announcements" },
  { to: "/admin/cms", icon: Settings, label: "CMS", testid: "admin-nav-cms" },
  { to: "/admin/experience", icon: Sparkles, label: "Experience", testid: "admin-nav-experience" },
  { to: "/admin/parking-libresakay", icon: ParkingCircle, label: "Parking & Libresakay", testid: "admin-nav-parking-libresakay" },
  { to: "/admin/safety", icon: ShieldAlert, label: "Safety & Risk", testid: "admin-nav-safety" },
  { to: "/admin/faqs", icon: HelpCircle, label: "FAQ List", testid: "admin-nav-faqs" },
  { to: "/admin/feedback", icon: MessageSquareHeart, label: "Feedback", testid: "admin-nav-feedback" },
];

export default function AdminShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const Sidebar = (
    <aside
      className="w-64 shrink-0 bg-white border-r-2 border-[#3D1E6D] h-screen sticky top-0 flex flex-col"
      data-testid="admin-sidebar"
    >
      <div className="p-5 border-b-2 border-[#3D1E6D]">
        <Logo />
        <div className="mt-3 text-[10px] font-bold tracking-[0.3em] uppercase text-[#E5247C]">
          Command Center
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {items.map(({ to, icon: Icon, label, testid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 border-2 transition-all font-bold text-sm uppercase tracking-wider ${
                isActive
                  ? "border-[#3D1E6D] bg-[#FFB400] shadow-[3px_3px_0_#3D1E6D]"
                  : "border-transparent text-[#3D1E6D] hover:border-[#3D1E6D] hover:bg-[#FFF4E6]"
              }`
            }
          >
            <Icon className="h-4 w-4" strokeWidth={2.75} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t-2 border-[#3D1E6D]">
        <div className="px-3 py-2 mb-2">
          <div className="text-[10px] font-bold tracking-widest uppercase text-[#6B4E9E]">
            Signed in as
          </div>
          <div className="text-sm font-bold text-[#3D1E6D] truncate" data-testid="admin-user-name">
            {user?.name}
          </div>
        </div>
        <button
          onClick={() => {
            logout();
            navigate("/");
          }}
          className="w-full flex items-center gap-2 px-3 py-2 bg-[#3D1E6D] text-white border-2 border-[#3D1E6D] font-bold text-xs uppercase tracking-wider hover:bg-[#E5247C] transition-colors"
          data-testid="admin-logout"
        >
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#FFF4E6] flex">
      <div className="hidden lg:block">{Sidebar}</div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative h-full w-72">
            {Sidebar}
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 p-2 kf-border bg-white"
              aria-label="Close menu"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b-2 border-[#3D1E6D] px-4 py-3 flex items-center justify-between">
          <Logo size="sm" />
          <button
            onClick={() => setOpen(true)}
            className="kf-border bg-[#FFB400] p-2"
            aria-label="Open menu"
            data-testid="admin-menu-toggle"
          >
            <Menu className="h-4 w-4" strokeWidth={2.75} />
          </button>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
